import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ActionItem, SEVERITY_COLORS } from '../types';
import { urgencyToken } from '../logic';
import ReturnedLine from './ReturnedLine';

interface Props {
  item: ActionItem;
  now: Date;
  onBack: () => void;
  actionable: boolean;
  reopenable: boolean;
  onClaim: () => void;
  onResolve: () => void;
  onDismiss: () => void;
  onSnooze: () => void;
  onReopen: () => void;
}

function anchorLabel(item: ActionItem): string {
  return item.campaign_name ?? item.unit_label ?? item.store ?? item.item_id;
}

export default function DetailHeader(p: Props) {
  const { item, now } = p;
  const urgency = urgencyToken(item, now);
  return (
    <Box sx={{
      position: 'sticky', top: 0, zIndex: 2, bgcolor: '#FFFFFF',
      borderBottom: '1px solid #E2E8F0', px: 3, py: 1.5,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={p.onBack}>Queue (Esc)</Button>
        <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEVERITY_COLORS[item.severity] }} />
        {item.severity === 'critical' && <Typography variant="caption" sx={{ color: '#DC2626', fontWeight: 700, letterSpacing: '0.04em' }}>CRITICAL</Typography>}
        {item.store && <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{item.store}</Typography>}
        <Chip label={item.status} size="small" sx={{ textTransform: 'capitalize' }}
          color={item.status === 'done' ? 'success' : item.status === 'dismissed' ? 'default' : 'primary'} variant="outlined" />
        {urgency && <Chip label={urgency.text} size="small" color={urgency.overdue ? 'error' : 'default'} />}
        <Box sx={{ flex: 1 }} />
        {p.actionable && (
          <>
            <Button variant="outlined" size="small" onClick={p.onClaim}>{item.status === 'claimed' ? 'Unclaim (C)' : 'Claim (C)'}</Button>
            <Button variant="contained" size="small" onClick={p.onResolve}>Done (D)</Button>
            <Button variant="outlined" size="small" onClick={p.onDismiss}>Dismiss (X)</Button>
            <Button variant="outlined" size="small" onClick={p.onSnooze}>Snooze (S)</Button>
          </>
        )}
        {p.reopenable && <Button variant="contained" size="small" onClick={p.onReopen}>Reopen (R)</Button>}
        {item.deep_link && (
          <Button size="small" endIcon={<OpenInNewIcon />} href={item.deep_link} target="_blank" rel="noopener">
            Open in {item.funnel_step === 'traffic' ? 'Google Ads' : 'Shopify'}
          </Button>
        )}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mt: 1, color: '#0F172A' }}>{anchorLabel(item)}</Typography>
      <ReturnedLine returned={item.card?.returned} variant="detail" store={item.store} />
    </Box>
  );
}
